import { ChevronRight, Folder, FolderOpen, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { RecordGroupRef } from '@/types/api/record-group'
import { cn } from '@/utils/cn'

import { getGuideOffset, getItemPadding } from './constants'

export function RecordTreeRow({
  id,
  label,
  depth,
  active = false,
  locked = false,
  traceOnly = false,
  isMainParent = false,
  expanded = false,
  onToggle,
  actions,
  breadcrumbPath,
}: {
  id: string
  label: string
  depth: number
  active?: boolean
  locked?: boolean
  traceOnly?: boolean
  isMainParent?: boolean
  expanded: boolean
  onToggle: () => void
  actions?: React.ReactNode
  breadcrumbPath?: RecordGroupRef[]
}) {
  const Icon = expanded ? FolderOpen : Folder

  return (
    <div
      role="treeitem"
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex h-9 min-w-0 items-center gap-1 rounded-md pr-2 text-sm text-foreground hover:bg-accent',
        active && 'bg-accent font-medium text-accent-foreground',
        isMainParent && 'font-semibold',
        traceOnly && 'italic',
      )}
      style={{ paddingLeft: getItemPadding(depth) }}
    >
      {Array.from({ length: depth }, (_, level) => (
        <span
          key={level}
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 w-px bg-border/70"
          style={{ left: getGuideOffset(level) }}
        />
      ))}
      <button
        type="button"
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
        aria-expanded={expanded}
        onClick={onToggle}
        className="flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronRight className={cn('size-3.5 transition-transform', expanded && 'rotate-90')} aria-hidden="true" />
      </button>
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <Link
        to={`/record-groups/${id}`}
        state={{ recordGroupPreview: { id, name: label }, recordGroupBreadcrumbPath: breadcrumbPath }}
        preventScrollReset
        className="min-w-0 flex-1 truncate rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title={label}
      >
        {label}
      </Link>
      {traceOnly && <span className="text-xs text-muted-foreground">Limited access</span>}
      {locked && <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-label="Locked" />}
      {actions && (
        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {actions}
        </div>
      )}
    </div>
  )
}
